import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../../shared/api'

export interface Schuljahr {
  schuljahr: string      // e.g. '2024-2025'
  bundesland: string     // 'HH' | 'SH' | …
  von: string            // YYYY-MM-DD
  bis: string            // YYYY-MM-DD
  syncedAt: string
}

export interface SchuljahrPolicy {
  bundesland: string
  sommerferienVonOffsetDays: number | null
  sommerferienBisOffsetDays: number | null
  notes: string | null
  auditModifiedAt: string | null
  auditModifiedBy: string | null
}

export interface SchuljahrPolicyPatch {
  sommerferienVonOffsetDays?: number | null
  sommerferienBisOffsetDays?: number | null
  notes?: string | null
}

export function useSchuljahr(bundesland: string | null) {
  return useQuery({
    queryKey: ['schuljahr', bundesland],
    queryFn: () => {
      const qs = bundesland ? `?bundesland=${encodeURIComponent(bundesland)}` : ''
      return api.get<Schuljahr[]>(`/api/schuljahr${qs}`)
    },
    enabled: bundesland !== null,
    staleTime: 24 * 60 * 60_000,  // rarely changes
  })
}

export function useSchuljahrPolicies(bundesland?: string) {
  return useQuery({
    queryKey: ['schuljahr-policy', bundesland ?? 'all'],
    queryFn: () => {
      const qs = bundesland ? `?bundesland=${encodeURIComponent(bundesland)}` : ''
      return api.get<SchuljahrPolicy[]>(`/api/schuljahr/policy${qs}`)
    },
    staleTime: 60_000,
  })
}

export function useUpdateSchuljahrPolicy() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ bundesland, patch }: { bundesland: string; patch: SchuljahrPolicyPatch }) =>
      api.patch<{ ok: boolean; bundesland: string }>(`/api/schuljahr/policy/${encodeURIComponent(bundesland)}`, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['schuljahr-policy'] }),
  })
}
